import { useEffect, useMemo, useState } from 'react';

import { ChakraBox, InputGroupV2 } from '@equip.health/ui';

import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import ProviderUniversalSelect from '~/components/common/calendar/ProviderUniversalSelect';
import {
  type GroupClassBaseFieldProps,
  GROUP_CLASS_ACCORDION_TOP_LEVEL_ID,
} from '~/lib/types/classes/GroupClassAccordion';

export const GroupClassHostsField = ({
  isDisabled,
  onChange,
  value,
}: GroupClassBaseFieldProps<ProviderAutoCompleteOption[]>) => {
  const { currentUser } = useGlobalContext();

  const transformedValue = useMemo(() => {
    if (!value || value.length === 0) return null;

    return value.map((option) => ({
      ...option,
      avatarSrc: option.avatarSrc || '',
      isDisabled,
      isMe: option.value === currentUser?.userId,
      label: option.primaryText || '',
      primaryText: option.primaryText,
      secondaryText: Array.isArray(option.secondaryText)
        ? option.secondaryText.join(', ')
        : option.secondaryText || '',
      value: option.value,
    }));
  }, [value, isDisabled, currentUser?.userId]);

  const [selectedProviderValue, setSelectedProviderValue] = useState<
    UniversalSelectOption | UniversalSelectOption[] | null
  >(transformedValue);

  useEffect(() => {
    setSelectedProviderValue(transformedValue);
  }, [transformedValue]);

  const handleProviderSelectV2 = (
    providerOption:
      | ProviderUniversalSelectOption
      | ProviderUniversalSelectOption[],
  ) => {
    setSelectedProviderValue(providerOption);

    const selectedOptions = Array.isArray(providerOption)
      ? providerOption
      : [providerOption];

    const updatedItems: ProviderAutoCompleteOption[] = selectedOptions.map(
      (option) => ({
        avatar: option.avatar,
        firstName: option.firstName,
        isMe: option.externalId === currentUser?.userId,
        lastName: option.lastName,
        primaryText: option.primaryText,
        providerTypeDetails: option.providerTypeDetails,
        secondaryText: option.secondaryText ? [option.secondaryText] : [],
        value: option.value,
      }),
    );

    onChange(updatedItems);
  };

  return (
    <ChakraBox _focusWithin={{ zIndex: 3 }} width="100%">
      <InputGroupV2
        id={`${GROUP_CLASS_ACCORDION_TOP_LEVEL_ID}__hosts`}
        isRequired
        label="Host(s)"
        shouldShowAsteriskWhenRequired
      >
        <ProviderUniversalSelect
          handleSelect={handleProviderSelectV2}
          id={`${GROUP_CLASS_ACCORDION_TOP_LEVEL_ID}__select-provider`}
          isDisabled={isDisabled}
          isMultiSelect
          modalId={`${GROUP_CLASS_ACCORDION_TOP_LEVEL_ID}__select-hosts`}
          customPlaceholder="Please choose"
          selectedProvider={selectedProviderValue}
          shouldAddCalendarProfile
          shouldDisableProviderOptionsWithoutCalendarId
        />
      </InputGroupV2>
    </ChakraBox>
  );
};
